import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent]
 * @property {import('./slideshow').Slideshow} [slideshow]
 * @property {HTMLElement[]} [media]
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  /** @type {number} */
  #selectionRequestId = 0;

  /** @type {HTMLCanvasElement | undefined} */
  #imageHoldover;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(
      StandardEvents.productSelect,
      this.#handleProductSelect,
      { signal }
    );

    this.refs.zoomDialogComponent?.addEventListener(
      ThemeEvents.zoomMediaSelected,
      this.#handleZoomMediaSelected,
      { signal }
    );
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
    this.#imageHoldover?.remove();
  }

  /**
   * Handles a product select event.
   *
   * Uses the media ID supplied by the color swatch to select the
   * corresponding slide as soon as the option changes.
   *
   * @param {ProductSelectEvent} event
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element)) return;
    if (event.target.closest('product-card')) return;

    const selectedMediaId = event.detail?.optionMediaId;
    const selectionRequestId = ++this.#selectionRequestId;

    // Capture the painted slide synchronously, before any listener or async
    // work has a chance to expose an uncached target slide.
    this.#preserveCurrentImage();

    // Start loading the selected media immediately, but keep the current slide
    // visible until the replacement image is ready to paint.
    if (selectedMediaId) {
      void this.#selectMediaWhenReady(String(selectedMediaId), selectionRequestId);
    }

    event.promise
      .then(async ({ variant, detail }) => {
        if (!detail?.html) return;

        const { html, resource } = detail;

        /*
         * The swatch's data-option-media-id is the most reliable
         * source because variant can be null when only Color has
         * been selected and Size has not yet been selected.
         */
        const resolvedMediaId =
          variant?.featured_media?.id ||
          variant?.featuredMedia?.id ||
          resource?.featured_media?.id ||
          resource?.featuredMedia?.id ||
          resource?.selected_or_first_available_variant?.featured_media?.id ||
          detail?.optionMediaId;

        if (!resolvedMediaId) return;

        const mediaId = String(resolvedMediaId);

        // The current gallery normally contains every product media item.
        if (await this.#selectMediaWhenReady(mediaId, selectionRequestId)) return;

        /*
         * Variant-scoped galleries may omit unselected variant images. In that
         * case, use Shopify's freshly rendered gallery, where the selected
         * variant media has already been sorted to the first position.
         */
        const newMediaGallery = html.querySelector('media-gallery');

        if (!newMediaGallery) return;

        const targetSlide = Array.from(newMediaGallery.querySelectorAll('slideshow-slide')).find(
          (slide) => slide.getAttribute('slide-id') === mediaId
        );

        if (!targetSlide) {
          console.warn('[media-gallery] Could not find selected media:', mediaId);
          return;
        }

        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn(
            '[media-gallery] Event promise rejected:',
            error
          );
        }
      })
      .finally(() => {
        if (selectionRequestId !== this.#selectionRequestId) return;
        this.#imageHoldover?.remove();
        this.#imageHoldover = undefined;
      });
  };

  /**
   * Loads an image in a hidden/lazy slide before making that slide visible.
   * This prevents the gallery background from flashing while a variant image
   * is being downloaded. A request id ensures rapid swatch clicks cannot make
   * an older image replace the shopper's latest selection.
   * @param {string} mediaId
   * @param {number} requestId
   * @returns {Promise<boolean>}
   */
  async #selectMediaWhenReady(mediaId, requestId) {
    const targetSlide = Array.from(this.querySelectorAll('slideshow-slide')).find(
      (slide) => slide.getAttribute('slide-id') === mediaId
    );

    if (!targetSlide) return false;

    this.#preserveCurrentImage();

    const image = targetSlide.querySelector('img');

    if (image && (!image.complete || image.naturalWidth === 0)) {
      image.loading = 'eager';
      image.fetchPriority = 'high';

      await new Promise((resolve) => {
        const finish = () => resolve(undefined);
        image.addEventListener('load', finish, { once: true });
        image.addEventListener('error', finish, { once: true });
        image.decode?.().then(finish).catch(() => undefined);
        if (image.complete && image.naturalWidth > 0) finish();
      });
    } else if (image) {
      // Decoding an already downloaded image avoids a one-frame blank on some
      // browsers. A decode failure is harmless because the image is complete.
      await image.decode?.().catch(() => undefined);
    }

    if (requestId !== this.#selectionRequestId) return true;

    const wasSelected = this.#selectMedia(mediaId);

    // The decoded target can still need a frame before it is composited. Keep
    // the previous image covering the gallery until that paint has happened.
    await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));

    if (requestId === this.#selectionRequestId) {
      this.#imageHoldover?.remove();
      this.#imageHoldover = undefined;
    }

    return wasSelected;
  }

  /**
   * Places a visual copy of the active image over the gallery while the next
   * swatch image loads. Repeated rapid selections retain the original painted
   * image instead of copying an intermediate, unfinished slide.
   */
  #preserveCurrentImage() {
    if (this.#imageHoldover?.isConnected) return;

    const currentSlide = this.slideshow?.slides?.[this.slideshow.current];
    let currentImage = currentSlide?.querySelector('img');

    // Fall back to the loaded image occupying the largest visible area. Some
    // gallery setups can temporarily have more than one aria-visible slide.
    if (!(currentImage instanceof HTMLImageElement) || currentImage.naturalWidth === 0) {
      currentImage = Array.from(this.querySelectorAll('slideshow-slide img'))
        .filter((image) => image instanceof HTMLImageElement && image.naturalWidth > 0)
        .sort((a, b) => {
          const aRect = a.getBoundingClientRect();
          const bRect = b.getBoundingClientRect();
          return bRect.width * bRect.height - aRect.width * aRect.height;
        })[0];
    }

    if (!(currentImage instanceof HTMLImageElement) || currentImage.naturalWidth === 0) return;

    const rect = currentImage.getBoundingClientRect();
    const styles = getComputedStyle(currentImage);
    const holdover = document.createElement('canvas');
    const pixelRatio = Math.min(window.devicePixelRatio || 1, 2);
    holdover.width = Math.max(1, Math.round(rect.width * pixelRatio));
    holdover.height = Math.max(1, Math.round(rect.height * pixelRatio));
    holdover.setAttribute('aria-hidden', 'true');

    const context = holdover.getContext('2d');
    if (!context) return;

    context.scale(pixelRatio, pixelRatio);
    context.fillStyle = styles.backgroundColor === 'rgba(0, 0, 0, 0)' ? 'transparent' : styles.backgroundColor;
    context.fillRect(0, 0, rect.width, rect.height);

    const naturalRatio = currentImage.naturalWidth / currentImage.naturalHeight;
    const boxRatio = rect.width / rect.height;
    const shouldCover = styles.objectFit === 'cover';
    const useWidth = shouldCover ? naturalRatio < boxRatio : naturalRatio > boxRatio;
    const drawWidth = useWidth ? rect.width : rect.height * naturalRatio;
    const drawHeight = useWidth ? rect.width / naturalRatio : rect.height;
    const drawX = (rect.width - drawWidth) / 2;
    const drawY = (rect.height - drawHeight) / 2;

    context.drawImage(currentImage, drawX, drawY, drawWidth, drawHeight);

    Object.assign(holdover.style, {
      position: 'fixed',
      inset: 'auto',
      top: `${rect.top}px`,
      left: `${rect.left}px`,
      width: `${rect.width}px`,
      height: `${rect.height}px`,
      margin: '0',
      objectFit: styles.objectFit,
      objectPosition: styles.objectPosition,
      borderRadius: styles.borderRadius,
      display: 'block',
      opacity: '1',
      visibility: 'visible',
      backgroundColor: styles.backgroundColor,
      zIndex: '2147483646',
      pointerEvents: 'none',
    });

    this.#imageHoldover = holdover;
    document.body.append(holdover);
  }

  /**
   * Selects a media item already rendered in this gallery.
   * @param {string} mediaId
   * @returns {boolean}
   */
  #selectMedia(mediaId) {
    const targetSlide = Array.from(this.querySelectorAll('slideshow-slide')).find(
      (slide) => slide.getAttribute('slide-id') === mediaId
    );

    if (!targetSlide) return false;

    this.slideshow?.select({ id: mediaId }, undefined, { animate: false });
    this.#selectGridMedia(mediaId);
    return true;
  }

  /**
   * Selects/highlights the media attached to the selected variant
   * when the gallery is using the desktop grid presentation.
   *
   * @param {string} mediaId
   */
  #selectGridMedia(mediaId) {
    const gridMedia = this.refs.media;

    if (!gridMedia?.length) return;

    const targetMedia = gridMedia.find((media) => {
      const mediaIdFromElement =
        media.getAttribute('data-media-id') ||
        media
          .querySelector('[data-media-id]')
          ?.getAttribute('data-media-id');

      return mediaIdFromElement === mediaId;
    });

    if (!targetMedia) return;

    targetMedia.scrollIntoView({
      behavior: 'instant',
      block: 'nearest',
      inline: 'nearest',
    });
  }

  /**
   * Handles the 'zoom-media:selected' event.
   *
   * @param {ZoomMediaSelectedEvent} event
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(
      event.detail.index,
      undefined,
      { animate: false }
    );
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index
   * @param {PointerEvent} event
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   *
   * @param {number} index
   */
  preloadImage(index) {
    const zoomDialogMedia =
      this.refs.zoomDialogComponent?.refs.media[index];

    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(
      zoomDialogMedia
    );
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
